import { v4 as uuid } from 'uuid';
import { isValidFnr } from '../aktor/validation';
import { NORWAY } from '../keys-constants';

export const getSkatterelasjonFields = () => ({
    relasjonId: uuid(),
    land: null,
    skattenummer: null,
    skattenummerMangler: null,
});

export const getReelleRettighetshaverFields = () => ({
    navn: null,
    fodeland: null,
    statsborgerskap: null,
    sekundartStatsborgerskap: null,
    fnr: null,
    adresse: null,
    postnummer: null,
    poststed: null,
    bostedsland: null,
    epost: null,
    mobil: null,
    eierandelProsent: null,
    bestemmendeKontroll: null,
    bestemmendeKontrollType: null,
    pep: null,
    pepVerv: null,
    pepRelasjon: null,
    skatterelasjonUsa: null,
    skatterelasjonUsaTin: null,
    skatterelasjonUsaTinMangler: null,
    skatterelasjonerFlere: null,
    skatterelasjoner: [getSkatterelasjonFields()],
});

const isFalsy = (val) => !val;

const hasValueWithLength = (val) => val && val.trim().length > 0;
/**
 * Test whether input matches a valid date pattern of dd.MM.yyyy
 */
const isValidDate = (value) => /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[012])\.(19\d{2}|20\d{2})$/.test(value);

// TIN består av tall og skal være på formatet XXX-XX-XXXX
const isValidTIN = (val) => val && /^\d{3}-\d{2}-\d{4}$/.test(val);

const hasValidTinOrExcuse = (tinVal, excuseVal) =>
    isValidTIN(tinVal) || (isFalsy(tinVal) && hasValueWithLength(excuseVal));

export const isValidSkatterelasjon = (rel) =>
    hasValueWithLength(rel.land) &&
    (hasValueWithLength(rel.skattenummer) || hasValueWithLength(rel.skattenummerMangler));

export const answersToTaxRelation = (answers) =>
    Object.keys(getSkatterelasjonFields()).reduce(
        (previousValue, key) => ({
            ...previousValue,
            [key]: answers[key],
        }),
        {}
    );

export const answersToBeneficialOwner = (answers) =>
    Object.keys(getReelleRettighetshaverFields()).reduce(
        (previousValue, key) => ({
            ...previousValue,
            [key]: answers[key],
        }),
        {}
    );

const shouldAcceptDate = (beneficialOwner) =>
    !(beneficialOwner.statsborgerskap === NORWAY || beneficialOwner.sekundartStatsborgerskap === NORWAY);

export const isValid = (
    key,
    beneficialOwnerOrTaxRelation = getReelleRettighetshaverFields(),
    config,
    norwegianPostalCodes
) => {
    const val = beneficialOwnerOrTaxRelation[key];
    switch (key) {
        case 'fnr':
            return isValidFnr(val) || (shouldAcceptDate(beneficialOwnerOrTaxRelation) && isValidDate(val));
        /* type text */
        case 'navn':
        case 'fodeland':
        case 'statsborgerskap':
        case 'land':
            return hasValueWithLength(val);
        case 'postnummer':
            if (config && config.addressInformationOptional) return true;
            if (norwegianPostalCodes && beneficialOwnerOrTaxRelation.bostedsland === NORWAY)
                return norwegianPostalCodes.includes(val);
            return true;
        case 'adresse':
        case 'poststed':
        case 'bostedsland':
            if (config && config.addressInformationOptional) return true;
            return hasValueWithLength(val);
        /* Mutually excluding */
        case 'skattenummer':
            return hasValueWithLength(val) || hasValueWithLength(beneficialOwnerOrTaxRelation.skattenummerMangler);
        case 'skattenummerMangler':
            return hasValueWithLength(val) || hasValueWithLength(beneficialOwnerOrTaxRelation.skattenummer);
        /* Text or number */
        case 'eierandelProsent':
            return typeof val === 'number' ? val > -1 && val <= 100 : val <= 100 && hasValueWithLength(val);
        case 'eierandelProsentCumulated':
            return val <= 100;
        /* Radio */
        case 'bestemmendeKontroll':
        case 'pep':
            return typeof val === 'boolean';
        case 'skatterelasjonerFlere':
            if (config && config.rrHideCRS) return true;
            return typeof val === 'boolean';
        /* Lists */
        case 'skatterelasjoner':
            if (config && config.rrHideCRS) return true;
            return beneficialOwnerOrTaxRelation.skatterelasjonerFlere === true
                ? val && val.filter((entry) => !isValidSkatterelasjon(entry)).length === 0
                : true;
        /* Nested */
        case 'bestemmendeKontrollType':
            return beneficialOwnerOrTaxRelation.bestemmendeKontroll === true ? hasValueWithLength(val) : true;
        case 'pepVerv':
        case 'pepRelasjon':
            return beneficialOwnerOrTaxRelation.pep === true ? hasValueWithLength(val) : true;
        case 'skatterelasjonUsa':
            if (config && config.rrHideFatca) return true;
            return typeof val === 'boolean';
        /* Nested mutually excluding */
        case 'skatterelasjonUsaTin':
            if (config && config.rrHideFatca) return true;
            return beneficialOwnerOrTaxRelation.skatterelasjonUsa === true
                ? hasValidTinOrExcuse(val, beneficialOwnerOrTaxRelation.skatterelasjonUsaTinMangler)
                : true;
        case 'skatterelasjonUsaTinMangler':
            if (config && config.rrHideFatca) return true;
            return beneficialOwnerOrTaxRelation.skatterelasjonUsa === true
                ? hasValidTinOrExcuse(beneficialOwnerOrTaxRelation.skatterelasjonUsaTin, val)
                : true;
        default:
            /* Not required pass ok */
            // sekundartStatsborgerskap'
            // epost'
            // mobil'
            return true;
    }
};

export const getTaxRelationErrors = (rel, config, postalCodeList) => {
    const keys = Object.keys(rel);
    const taxRelation = answersToTaxRelation(rel);
    return keys.filter((key) => !isValid(key, taxRelation, config, postalCodeList));
};
